import { observer } from "mobx-react-lite";
import { FunctionComponent } from "react";

import { DisplayOutcome } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { DisplayOutcomePartiallyResolved } from "@modules/clinical/screens/patient-record/components/SOTAP/discharge-form/DisplayOutcomePartiallyResolved.tsx";
import { DisplayOutcomeResolved } from "@modules/clinical/screens/patient-record/components/SOTAP/discharge-form/DisplayOutcomeResolved.tsx";
import { DisplayOutcomeUnresolved } from "@modules/clinical/screens/patient-record/components/SOTAP/discharge-form/DisplayOutcomeUnresolved.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { useCardFormLayoutContext } from "@ui-components/card-form-layout/context/CardFormLayoutHelperContext.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { ButtonsGroupSingleChoiceField } from "@ui-components/form/ButtonsGroupSingleChoiceField.tsx";
import { DatePickerField } from "@ui-components/form/DatePickerField.tsx";
import { FieldCondition } from "@ui-components/form/FieldCondition.tsx";
import { Fieldset } from "@ui-components/form/Fieldset/Fieldset.tsx";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";

import {
  ClaimDischargeCardIds,
  ClaimDischargeSideRailLabels
} from "../../claim/components/ClaimFormEnums.ts";
import { ClaimCard } from "../../shared-components/ClaimCard.tsx";
import { ClaimDischargeFormValues } from "../types/dischargeFormValues.type.ts";

export const ClaimDischargeOutcomeBase: FunctionComponent = observer(() => {
  const { clinical } = useStores();
  const { selectedMenuItemId } = useCardFormLayoutContext();
  const nameOf = nameOfFactory<ClaimDischargeFormValues>();

  return (
    <ClaimCard
      id={ClaimDischargeCardIds.Outcome}
      heading={ClaimDischargeSideRailLabels.Outcome}
      iconName="ComplianceAudit"
      openOnRender={selectedMenuItemId === ClaimDischargeCardIds.Outcome}
    >
      <Fieldset
        tokens={{
          childrenGap: 8
        }}
      >
        <DatePickerField
          name={nameOf("dischargeDate")}
          label="Discharge date"
          required
        />
        <ButtonsGroupSingleChoiceField
          label="Discharger"
          name={nameOf("discharger")}
          options={clinical.ref.dischargers.keyTextValues.map(x => {
            return {
              key: x.key,
              text: x.text,
              disabled: x.key === "PRO" // Disable Provider
            };
          })}
          required
        />
        <ButtonsGroupSingleChoiceField
          label="Outcome"
          name={nameOf("dischargeOutcome")}
          options={clinical.ref.dischargerOutcomes.keyTextValues}
          required
        />
        <FieldCondition
          when={nameOf("dischargeOutcome")}
          is={DisplayOutcome.Resolved}
        >
          <DisplayOutcomeResolved />
        </FieldCondition>
        <FieldCondition
          when={nameOf("dischargeOutcome")}
          is={DisplayOutcome.PartiallyResolved}
        >
          <DisplayOutcomePartiallyResolved />
        </FieldCondition>
        <FieldCondition
          when={nameOf("dischargeOutcome")}
          is={DisplayOutcome.Unresolved}
        >
          <DisplayOutcomeUnresolved />
        </FieldCondition>
        <TextInputField
          label="Notes"
          name={nameOf("notes")}
          multiline
          rows={2}
          resizable={false}
          maxLength={255}
        />
      </Fieldset>
    </ClaimCard>
  );
});

export const ClaimDischargeOutcomeSection = withFetch(
  x => [
    x.clinical.ref.dischargerOutcomes.load(),
    x.clinical.ref.dischargers.load(),
    x.clinical.ref.outcomesDetails.load()
  ],
  ClaimDischargeOutcomeBase
);
